import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { projectService, reportsService } from 'api';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { formatDuration } from 'shared/lib/formatDuration';
import { queryKeys } from 'shared/constants/queryKeys';
import { ROUTE_PATHS } from 'shared/constants/routes';
import { usePermissions } from 'shared/hooks/usePermissions';

import {
  DetailHero,
  DetailHeroBreadcrumbLink,
  DetailHeroBreadcrumbs,
  DetailHeroColorDot,
  DetailHeroContent,
  DetailHeroDecorBottom,
  DetailHeroDecorTop,
  DetailHeroPill,
  DetailHeroPillsRow,
  DetailHeroStatCard,
  DetailHeroStatLabel,
  DetailHeroStatsGrid,
  DetailHeroStatValue,
  DetailHeroSubtitle,
  DetailHeroTitle,
  DetailHeroTitleRow,
  LoadingContainer,
  PillTab,
  PillTabsWrapper,
  ProjectDetailPageRoot,
} from '../projects.styles';

import ProjectAccessTab from './ProjectAccessTab';
import ProjectSettingsTab from './ProjectSettingsTab';

const TABS = ['Members', 'Settings'] as const;

const ProjectDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const { canEditProject } = usePermissions();
  const visibleTabs = TABS.filter((t) => t !== 'Settings' || canEditProject);

  const { data: project, isLoading } = useQuery({
    queryKey: queryKeys.projects.detail(id!),
    queryFn: () => projectService.getProject(id!).then((res) => res.data.data),
    enabled: Boolean(id),
  });

  const { data: members = [] } = useQuery({
    queryKey: queryKeys.projects.members(id!),
    queryFn: () => projectService.getProjectMembers(id!).then((res) => res.data.data),
    enabled: Boolean(id),
  });

  // Backend requires from/to on the summary endpoint — use the widest practical range
  // to approximate all-time totals for this project. project.getProject() doesn't return
  // trackedHours (only the list endpoint does), so Total Tracked is derived from this
  // same summary response rather than a separate call.
  const ALL_TIME_FROM = '2000-01-01';
  const allTimeTo = new Date().toISOString().slice(0, 10);

  const { data: summaryStats } = useQuery({
    queryKey: queryKeys.reports.summary({
      projectId: id,
      groupBy: 'project',
      from: ALL_TIME_FROM,
      to: allTimeTo,
    }),
    queryFn: () =>
      reportsService
        .getSummary({ projectId: id!, groupBy: 'project', from: ALL_TIME_FROM, to: allTimeTo })
        .then((res) => {
          const group = res.data.data.groups[0];
          return { entryCount: group?.entryCount ?? 0, totalDurationSecs: group?.totalDurationSecs ?? 0 };
        }),
    enabled: Boolean(id),
  });
  const entryCount = summaryStats?.entryCount ?? 0;
  const totalTrackedSecs = summaryStats?.totalDurationSecs ?? 0;

  if (isLoading) {
    return (
      <LoadingContainer>
        <CircularProgress />
      </LoadingContainer>
    );
  }

  return (
    <ProjectDetailPageRoot>
      <Box>
        <DetailHero>
          <DetailHeroDecorTop aria-hidden />
          <DetailHeroDecorBottom aria-hidden />
          <DetailHeroContent>
            <DetailHeroBreadcrumbs separator={<ChevronRightIcon sx={{ fontSize: 14 }} />}>
              <DetailHeroBreadcrumbLink underline="hover" onClick={() => navigate(ROUTE_PATHS.PROJECTS)}>
                Projects
              </DetailHeroBreadcrumbLink>
              <Typography color="#fff" fontSize={14} fontWeight={500}>
                {project?.name ?? ''}
              </Typography>
            </DetailHeroBreadcrumbs>

            <DetailHeroTitleRow>
              <DetailHeroColorDot dotcolor={project?.color ?? '#6E4FF7'} />
              <div>
                <DetailHeroTitle>{project?.name ?? ''}</DetailHeroTitle>
                <DetailHeroSubtitle>{project?.clientName ?? 'Internal project'}</DetailHeroSubtitle>
              </div>
              <DetailHeroPillsRow>
                <DetailHeroPill>{project?.isArchived ? 'Archived' : 'Active'}</DetailHeroPill>
                <DetailHeroPill muted>{project?.isPublic ? 'Public' : 'Private'}</DetailHeroPill>
              </DetailHeroPillsRow>
            </DetailHeroTitleRow>

            <DetailHeroStatsGrid>
              <DetailHeroStatCard>
                <DetailHeroStatLabel>Total Tracked</DetailHeroStatLabel>
                <DetailHeroStatValue>
                  {formatDuration(totalTrackedSecs)}
                </DetailHeroStatValue>
              </DetailHeroStatCard>
              <DetailHeroStatCard>
                <DetailHeroStatLabel>Sessions</DetailHeroStatLabel>
                <DetailHeroStatValue>{entryCount}</DetailHeroStatValue>
              </DetailHeroStatCard>
              <DetailHeroStatCard>
                <DetailHeroStatLabel>Members</DetailHeroStatLabel>
                <DetailHeroStatValue>{members.length}</DetailHeroStatValue>
              </DetailHeroStatCard>
              <DetailHeroStatCard>
                <DetailHeroStatLabel>Billing</DetailHeroStatLabel>
                <DetailHeroStatValue sx={{ fontSize: '1.05rem' }}>
                  {project?.isBillable ? 'Billable' : 'Non-billable'}
                </DetailHeroStatValue>
              </DetailHeroStatCard>
            </DetailHeroStatsGrid>
          </DetailHeroContent>
        </DetailHero>

        <PillTabsWrapper>
          {visibleTabs.map((label, index) => (
            <PillTab key={label} type="button" active={activeTab === index} onClick={() => setActiveTab(index)}>
              {label}
            </PillTab>
          ))}
        </PillTabsWrapper>
      </Box>

      {visibleTabs[activeTab] === 'Members' && project && <ProjectAccessTab projectId={id!} />}
      {visibleTabs[activeTab] === 'Settings' && project && (
        <ProjectSettingsTab projectId={id!} project={project} />
      )}
    </ProjectDetailPageRoot>
  );
};

export default ProjectDetailPage;
